/**
 * Reports auth failures to auth-api so they become Error.Auth.* events.
 *
 * Why this exists: almost all of the auth flow runs directly between this SPA and Cognito, so
 * sign-in, MFA and password-reset failures never reach our backend. Today they die in a
 * console.error, which is why a user who says "sisteme giremiyorum" leaves us nothing to look at
 * — most sharply for an expired temporary password, which the UI has been mistranslating as
 * "incorrect email or password".
 *
 * Two guarantees this class must keep, both covered by tests:
 *   1. It NEVER breaks or slows the auth flow. Fire-and-forget, swallows its own failures, and
 *      goes quiet permanently if the endpoint is unreachable.
 *   2. Exactly ONE event per failure, no matter how many layers catch and rethrow it.
 */

import { apiConfig } from '@/config/auth.config';
import { normalizeCognitoError } from '@/utils/error.utils';
import type {
  AuthErrorReportBody,
  ReporterStats,
  ReportInput,
  SuppressionReason,
} from '@/types/authEvent.types';
import { getTraceHeaders } from './correlation';
import { extractErrorFacts, sanitizeMessage } from './sanitize';
import { routeToSentry, setSentryUser } from './sentryBridge';

const ENDPOINT = '/auth/events';
const CLIENT_APP = 'bono-auth';
const REQUEST_TIMEOUT_MS = 3000;

const SESSION_CAP = 25;
const CAP_STORAGE_KEY = 'bono.auth.events.count';
const MAX_CONSECUTIVE_FAILURES = 3;

/**
 * Client-side validation that never reaches Cognito. Both are unreachable from the UI anyway
 * (the views gate on input length), so reporting them would be pure noise.
 */
const SUPPRESSED_CODES = new Set([
  'EmptyVerifyTOTPSetupCode',
  'EmptyConfirmResetPasswordUsername',
]);

/**
 * Marks an error object as already reported. A Symbol so it can never collide with a real
 * property, non-enumerable so it never serialises into a payload or a log.
 *
 * This is what makes "report at the layer that owns the failure" enforceable rather than a
 * convention. CognitoService.signIn rethrows, AuthService converts the throw into
 * `{success:false, error}`, and the view then translates that same error INSTANCE — so without
 * this, adding one well-meaning hook in a view double-counts every failure. A time-window
 * dedupe cannot fix that case, because the two reports can carry different stages.
 */
const REPORTED = Symbol('bono.auth.reported');

function isEnabledByEnv(): boolean {
  return import.meta.env.VITE_AUTH_EVENTS_ENABLED !== 'false';
}

function isDebug(): boolean {
  return import.meta.env.VITE_AUTH_EVENTS_DEBUG === 'true';
}

export class AuthEventReporter {
  private static enabled = isEnabledByEnv();

  /**
   * The email of whoever is currently trying to authenticate.
   *
   * Most Cognito calls don't receive one — confirmSignIn takes only a challenge response,
   * setUpTOTP takes nothing — yet those are exactly the flows where a user gets stuck and
   * support needs to know WHO. Rather than importing the Pinia store into CognitoService (which
   * would couple a leaf service to app state and break in tests), the orchestrator sets this
   * once when the email becomes known and report() falls back to it.
   *
   * In-memory and per tab; cleared on logout.
   */
  private static subjectEmail: string | null = null;

  private static consecutiveFailures = 0;
  private static sent = 0;
  private static suppressed = 0;
  private static failures = 0;

  /**
   * dedupeKey -> how many times this exact failure has happened this session.
   *
   * There is deliberately NO time window here. An earlier version suppressed any repeat inside
   * 2s, which sounds prudent but hides the failures that matter most: a redirect or guard loop
   * fires the same failure many times per second, and a time window would have discarded all of
   * it. The Symbol guard already covers the real duplicate case (one error instance seen by
   * several layers), so repeats reaching this point are genuine and get counted.
   */
  private static seen = new Map<string, { occurrences: number }>();

  /**
   * Fire-and-forget. Returns void SYNCHRONOUSLY and never throws.
   *
   * The signature is `void`, not `Promise<void>`, on purpose: a promise-returning reporter
   * invites `await AuthEventReporter.report(...)` on the hot login path, and invites unhandled
   * rejections. Returning void makes both mistakes impossible to write.
   */
  /** Call when the email becomes known, and with null on logout. */
  static setSubjectEmail(email: string | null): void {
    this.subjectEmail = email && email.trim() ? email.trim() : null;
    setSentryUser(this.subjectEmail);
  }

  static report(input: ReportInput): void {
    try {
      this.reportInternal(input);
    } catch (e) {
      // A failure to report a failure must never surface to the user.
      this.failures += 1;
      if (isDebug()) {
        console.warn('[auth-events] reporter threw and was contained', e);
      }
    }
  }

  private static reportInternal(input: ReportInput): void {
    if (!this.enabled) {
      return this.drop(input, 'disabled');
    }

    // Layered double-report guard. Must come before every other check so that a suppressed
    // duplicate cannot consume the occurrence ladder or the session cap.
    if (this.isAlreadyReported(input.error)) {
      return this.drop(input, 'already-reported');
    }
    this.markReported(input.error);

    const facts = extractErrorFacts(input.error);

    // ORDERING: normalise on the RAW message, sanitise after. See sanitize.ts.
    const normalised = input.error !== undefined
      ? normalizeCognitoError(input.error)
      : { name: input.errorCode ?? 'UnknownError', message: '' };
    const errorCode = input.errorCode ?? normalised.name;
    const errorDetail = sanitizeMessage(input.errorDetail ?? facts.message);

    if (SUPPRESSED_CODES.has(errorCode)) {
      return this.drop(input, 'suppression-list');
    }

    const email = input.email ?? this.subjectEmail;
    const dedupeKey = `${input.stage}|${input.step}|${errorCode}|${email ?? ''}`;
    const occurrenceCount = (this.seen.get(dedupeKey)?.occurrences ?? 0) + 1;
    this.seen.set(dedupeKey, { occurrences: occurrenceCount });

    // Occurrence ladder: send 1, 2, 3, then every 5th. The true count still rides along, so the
    // backend keeps an accurate tally without the traffic — which matters because we
    // deliberately did NOT enable idempotency server-side (that would collapse repeats into one
    // row and destroy the "how many times, and when" signal support needs).
    if (!this.shouldSendOccurrence(occurrenceCount)) {
      return this.drop(input, 'occurrence-ladder', occurrenceCount);
    }

    const count = this.bumpSessionCount();
    if (count > SESSION_CAP) {
      return this.drop(input, 'session-cap', occurrenceCount);
    }

    const body: AuthErrorReportBody = {
      stage: input.stage,
      errorCode,
      step: input.step,
      errorSource: input.errorSource ?? 'COGNITO',
      clientApp: CLIENT_APP,
      occurrenceCount,
      wasUserVisible: input.wasUserVisible ?? true,
      appStage: import.meta.env.VITE_APP_STAGE,
    };
    if (errorDetail) {
      body.errorDetail = errorDetail;
    }
    if (email) {
      body.email = email;
    }
    const httpStatus = input.httpStatus ?? facts.httpStatus;
    if (httpStatus != null) {
      body.httpStatus = httpStatus;
    }
    if (input.userMessageKey) {
      body.userMessageKey = input.userMessageKey;
    }
    const route = this.currentRoute();
    if (route) {
      body.location = route;
    }

    // Sentry gets its own routing decision — bug vs expected user error. See sentryBridge.
    routeToSentry({
      errorSource: body.errorSource!,
      errorCode,
      stage: input.stage,
      step: input.step,
      error: input.error,
      httpStatus: body.httpStatus ?? null,
    });

    this.sent += 1;
    if (isDebug()) {
      console.table([{ ...body, action: 'SENT' }]);
    }
    void this.send(body);
  }

  /** 1, 2, 3, then every 5th. Enough to see a pattern without reporting every keystroke. */
  private static shouldSendOccurrence(occurrence: number): boolean {
    return occurrence <= 3 || occurrence % 5 === 0;
  }

  private static async send(body: AuthErrorReportBody): Promise<void> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      await fetch(`${apiConfig.baseUrl}${ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTraceHeaders() },
        body: JSON.stringify(body),
        // Survives the unload that follows every auth redirect. SSOService and RedirectService
        // both assign window.location.href immediately; an XHR would be cancelled, this is not.
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: controller.signal,
      });
      this.consecutiveFailures = 0;
    } catch (e) {
      // No retry, on purpose. A retrying reporter turns an outage into a request storm against
      // the auth service — the one service that must stay up.
      this.failures += 1;
      this.consecutiveFailures += 1;
      if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        this.enabled = false;
        if (isDebug()) {
          console.warn('[auth-events] circuit opened after repeated transport failures');
        }
      }
      if (isDebug()) {
        console.warn('[auth-events] send failed (contained)', e);
      }
    } finally {
      clearTimeout(timer);
    }
  }

  private static isAlreadyReported(error: unknown): boolean {
    return typeof error === 'object' && error !== null
      && (error as Record<symbol, unknown>)[REPORTED] === true;
  }

  private static markReported(error: unknown): void {
    if (typeof error !== 'object' || error === null) {
      return;
    }
    try {
      Object.defineProperty(error, REPORTED, {
        value: true,
        enumerable: false,
        configurable: true,
        writable: false,
      });
    } catch {
      // Frozen or sealed error: fall through harmlessly rather than throw.
    }
  }

  /** sessionStorage so the cap survives a reload; per tab, so one tab cannot mute another. */
  private static bumpSessionCount(): number {
    try {
      const next = Number(sessionStorage.getItem(CAP_STORAGE_KEY) ?? '0') + 1;
      sessionStorage.setItem(CAP_STORAGE_KEY, String(next));
      return next;
    } catch {
      return 1;
    }
  }

  private static currentRoute(): string | null {
    try {
      return typeof window !== 'undefined' ? window.location.pathname : null;
    } catch {
      return null;
    }
  }

  private static drop(
    input: ReportInput,
    reason: SuppressionReason,
    occurrenceCount?: number,
  ): void {
    this.suppressed += 1;
    if (isDebug()) {
      console.table([{
        stage: input.stage,
        step: input.step,
        occurrenceCount: occurrenceCount ?? 1,
        action: 'SUPPRESSED',
        reason,
      }]);
    }
  }

  /** Test/verification seam. */
  static reset(): void {
    this.enabled = isEnabledByEnv();
    this.subjectEmail = null;
    this.consecutiveFailures = 0;
    this.sent = 0;
    this.suppressed = 0;
    this.failures = 0;
    this.seen.clear();
    try {
      sessionStorage.removeItem(CAP_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  static get stats(): ReporterStats {
    return {
      sent: this.sent,
      suppressed: this.suppressed,
      failures: this.failures,
      enabled: this.enabled,
    };
  }
}
